import { useState, useEffect, useRef } from 'react';
import { useLiveReactions } from '../hooks/useLiveReactions';
import EnergyBar from './EnergyBar';
import ReactionFeed from './ReactionFeed';
import LiveLeaderboard from './LiveLeaderboard';
import { Trophy } from 'lucide-react';

export default function LiveReactionPanel({ streamId, isHost = false, compact = false }) {
  const {
    REACTION_TYPES,
    energyState,
    reactionFeed,
    leaderboard,
    crowdGoingCrazy,
    sendReaction,
    cooldowns,
  } = useLiveReactions(streamId);

  const [floatingEmojis, setFloatingEmojis] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const emojiIdRef = useRef(0);

  const handleReaction = async (type, isSuper = false) => {
    const result = await sendReaction(type, isSuper);
    if (result) {
      // Spawn floating emoji
      const id = ++emojiIdRef.current;
      const rxn = REACTION_TYPES.find(r => r.type === type);
      setFloatingEmojis(prev => [...prev, {
        id,
        emoji: rxn?.emoji || '🔥',
        isSuper: result.is_super,
        x: 15 + Math.random() * 70,
      }]);
      setTimeout(() => {
        setFloatingEmojis(prev => prev.filter(e => e.id !== id));
      }, 2000);
    }
  };

  return (
    <>
      {/* Crowd Going Crazy Overlay */}
      {crowdGoingCrazy && (
        <div className="fixed inset-0 pointer-events-none z-[100]" data-testid="crowd-going-crazy">
          <div className="absolute inset-0 animate-pulse-border" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center animate-bounce-in">
            <p className="text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-red-500 to-pink-500 drop-shadow-2xl whitespace-nowrap">
              CROWD GOING CRAZY
            </p>
          </div>
        </div>
      )}

      {/* Floating Emojis */}
      <div className="fixed bottom-32 left-0 right-0 pointer-events-none z-[90]">
        {floatingEmojis.map(fe => (
          <span
            key={fe.id}
            className={`absolute animate-float-up ${fe.isSuper ? 'text-4xl' : 'text-2xl'}`}
            style={{ left: `${fe.x}%`, bottom: 0 }}
          >
            {fe.emoji}
          </span>
        ))}
      </div>

      {/* Energy Bar */}
      <EnergyBar state={energyState} compact={compact} />

      {/* On-screen reaction feed */}
      <ReactionFeed reactions={reactionFeed} />

      {/* Floating Reaction Panel */}
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-1.5 px-3 py-2 rounded-full bg-black/70 backdrop-blur-xl border border-white/10 shadow-2xl"
        data-testid="reaction-panel"
      >
        {REACTION_TYPES.map(rxn => {
          const now = Date.now();
          const onCooldown = cooldowns[rxn.type] && now - cooldowns[rxn.type] < 900;
          return (
            <button
              key={rxn.type}
              onClick={() => handleReaction(rxn.type)}
              onDoubleClick={() => handleReaction(rxn.type, true)}
              disabled={onCooldown}
              className={`group relative flex flex-col items-center transition-all duration-150 ${
                onCooldown ? 'opacity-40 scale-95' : 'hover:scale-125 active:scale-90'
              }`}
              title={`${rxn.label} (${rxn.emoji}) — Double-tap for Super`}
              data-testid={`reaction-btn-${rxn.type}`}
            >
              <span className="text-2xl md:text-3xl select-none">{rxn.emoji}</span>
              <span className="text-[9px] text-white/50 leading-none mt-0.5 hidden md:block">{rxn.label}</span>
              {/* Weight badge */}
              <span className="absolute -top-2 -right-1 text-[8px] font-bold text-amber-400 bg-black/80 rounded-full px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                +{rxn.weight}
              </span>
            </button>
          );
        })}

        {/* Leaderboard toggle */}
        <button
          onClick={() => setShowLeaderboard(!showLeaderboard)}
          className="ml-2 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          data-testid="leaderboard-toggle-btn"
          title="Top Supporters"
        >
          <Trophy className="w-4 h-4 text-amber-400" />
        </button>
      </div>

      {/* Leaderboard Overlay */}
      {showLeaderboard && (
        <LiveLeaderboard
          supporters={leaderboard}
          onClose={() => setShowLeaderboard(false)}
        />
      )}
    </>
  );
}
